import { promiseDelay } from '@iotize/common/promise';
import { ServiceCallRunner, TapResponse } from '@iotize/tap';
import { ResultCode, TapResponseFrame } from '@iotize/tap/client/api';
import { TapResponseFrameBuilder } from '@iotize/tap/client/impl';
import { MockClient } from './mock-client';

export function createMockServiceFunctionCustom<T, ArgType extends any[]>(
  responseBuilder: (...args: ArgType) => TapResponse<T>,
  options: {
    responseDelay: number;
  } = {
    responseDelay: 10,
  },
): (...args: ArgType) => Promise<TapResponse<T>> {
  return (...args: ArgType) => {
    return promiseDelay(responseBuilder(...args), options.responseDelay);
  };
}

export function createMockServiceFunction<T = void>(
  responseStatus: number = ResultCode.OK,
  options: {
    responseDelay: number;
  } = {
    responseDelay: 10,
  },
) {
  return () => {
    const response = createMockServiceResponse<T>(responseStatus);
    return promiseDelay(response, options.responseDelay);
  };
}

export function createMockServiceFunctionWithBody<T>(
  responseStatus: number = ResultCode.OK,
  body: T,
  options: {
    responseDelay: number;
  } = {
    responseDelay: 10,
  },
) {
  return () => {
    const response = createMockServiceResponseWithBody(responseStatus, body);
    return promiseDelay(response, options.responseDelay);
  };
}
export function createMockServiceResponse<T = void>(
  responseStatus: number = ResultCode.OK,
): TapResponse<T> {
  const response = new TapResponse<T>(
    TapResponseFrameBuilder.create(responseStatus),
  );
  return response;
}

export function createMockServiceResponseWithBody<T>(
  responseStatus: number = ResultCode.OK,
  body: T,
): TapResponse<T> {
  const response = new TapResponse<T>(
    TapResponseFrameBuilder.create(responseStatus),
  );
  if (body instanceof Uint8Array) {
    (response['_tapResponse'] as TapResponseFrame).data = body;
  }
  if (body) {
    response.setBody(body);
  }
  return response;
}

export function createMockServiceResponseError<T = void>(code: number) {
  const response = new TapResponse<T>(TapResponseFrameBuilder.ERROR(code));
  return response;
}

export function createMockService<T>(
  constructorFct: new (serviceCallRunner: ServiceCallRunner) => T,
) {
  const serviceCallRunnner = new MockServiceCallRunner();
  return new constructorFct(serviceCallRunnner);
}

export class MockServiceCallRunner extends ServiceCallRunner {
  // TODO
  constructor() {
    super(new MockClient() as any);
  }
}
